import React, { useEffect, useState } from 'react';
import { fetchFromDentalAPI } from './common';
import './LocationOnboarding.css';

const labels = { complete: 'Complete', incomplete: 'Incomplete', unknown: 'Unknown', not_applicable: 'Not applicable' };

export default function LocationOnboarding() {
  const [practices, setPractices] = useState([]);
  const [search, setSearch] = useState('');
  const [practiceId, setPracticeId] = useState('');
  const [status, setStatus] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [listRefresh, setListRefresh] = useState(0);
  const [onlyOutstanding, setOnlyOutstanding] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadingList(true);
    setListError('');
    fetchFromDentalAPI('/api/onboarding-locations').then(data => {
      if (!active) return;
      setPractices(data.practices.slice().sort((a, b) => (a.name || a.practiceId).localeCompare(b.name || b.practiceId)));
    }).catch(() => { if (active) setListError('Could not load locations. Please try again.'); })
      .finally(() => { if (active) setLoadingList(false); });
    return () => { active = false; };
  }, [listRefresh]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let active = true;
    let timer;
    const check = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchFromDentalAPI(`/api/onboarding-locations/${encodeURIComponent(practiceId)}`);
        if (active) setStatus(data);
      } catch (_) {
        if (active) { setStatus(null); setError('Could not verify this location. Please retry.'); }
      } finally {
        if (active) {
          setLoading(false);
          timer = setTimeout(check, 60000);
        }
      }
    };
    setStatus(null);
    check();
    return () => { active = false; clearTimeout(timer); };
  }, [practiceId, refresh]);

  const query = search.trim().toLowerCase();
  const filtered = practices.filter(p => query.split(/\s+/).every(term =>
    `${p.name || ''} ${p.practiceId} ${p.organizationId || ''}`.toLowerCase().includes(term)))
    .sort((a, b) => Number(b.practiceId.toLowerCase() === query) - Number(a.practiceId.toLowerCase() === query));
  const selectLocation = id => {
    if (id === practiceId) return;
    setStatus(null);
    setPracticeId(id);
  };
  // A step shared by two groups (the PMS connection) counts once in the page total.
  const tally = groups => {
    const unique = [...new Map(groups.flatMap(group => group.steps).map(step => [step.id, step])).values()];
    const applicable = unique.filter(step => step.status !== 'not_applicable');
    return { done: applicable.filter(step => step.status === 'complete').length, total: applicable.length };
  };
  const { done: complete, total: applicableCount } = tally(status?.groups || []);
  const coreGroups = status?.groups.filter(group => !group.section) || [];
  const paymentsGroups = status?.groups.filter(group => group.section === 'payments') || [];
  const insuranceGroups = status?.groups.filter(group => group.section === 'insurance') || [];
  const paymentsTally = tally(paymentsGroups);
  const insuranceTally = tally(insuranceGroups);
  const coverage = status?.insurance?.coverage;
  const renderGroups = groups => <div className="lo-groups">{groups.map(group => {
    const relevant = group.steps.filter(step => step.status !== 'not_applicable');
    const done = relevant.filter(step => step.status === 'complete').length;
    const visible = group.steps.filter(step => !onlyOutstanding || ['incomplete', 'unknown'].includes(step.status));
    return <article className="lo-group" key={group.id}><header><h2>{group.title}</h2><span>{done} / {relevant.length}</span></header>
      <progress aria-label={`${group.title} completion`} value={done} max={relevant.length || 1} />
      <ul>{visible.map(step => <li key={step.id}><div className="lo-step-title"><h3>{step.label}</h3><span className={`lo-badge lo-${step.status}`}>{labels[step.status]}</span></div><p>{step.detail}</p></li>)}</ul>
      {!visible.length && <p className="lo-all-done">All applicable steps complete.</p>}
    </article>;
  })}</div>;

  return <section className="location-onboarding">
    <header className="lo-header"><div><p className="lo-eyebrow">LOCATION READINESS</p><h1>Onboarding checklist</h1><p>Track Phones, Sophia inbound, Sophia outbound, Insurance and Payments from current location data.</p></div>
      <button type="button" onClick={() => setRefresh(value => value + 1)} disabled={!practiceId || loading}>{loading ? 'Checking…' : 'Refresh status'}</button>
    </header>
    <div className="lo-selectors">
      <label>Find a location<input type="search" placeholder="Search name, location ID or organization" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter' && query && filtered.length && (filtered.length === 1 || filtered[0].practiceId.toLowerCase() === query)) {
          e.preventDefault();
          selectLocation(filtered[0].practiceId);
        }
      }} /></label>
      <label>Location<select value={practiceId} disabled={loadingList} onChange={e => selectLocation(e.target.value)}>
        <option value="">{loadingList ? 'Loading locations…' : 'Select a location'}</option>
        {practiceId && !filtered.some(p => p.practiceId === practiceId) && practices.filter(p => p.practiceId === practiceId).map(p => <option key={p.practiceId} value={p.practiceId}>{p.name || p.practiceId} · {p.practiceId}</option>)}
        {filtered.map(p => <option key={p.practiceId} value={p.practiceId}>{p.name || p.practiceId} · {p.practiceId}</option>)}
      </select></label>
    </div>
    {query && !loadingList && !listError && filtered.length > 0 && <div className="lo-search-results">
      <p role="status">{filtered.length} matching location{filtered.length === 1 ? '' : 's'} — select one to open its checklist.</p>
      <ul>{filtered.slice(0, 20).map(p => <li key={p.practiceId}><button type="button" onClick={() => selectLocation(p.practiceId)} aria-pressed={practiceId === p.practiceId}>
        <strong>{p.name || p.practiceId}</strong><span>{p.practiceId}</span>
      </button></li>)}</ul>
      {filtered.length > 20 && <p>Showing the first 20 matches. Refine your search or use the location dropdown for all matches.</p>}
    </div>}
    {listError && <div role="alert" className="lo-error">{listError} <button onClick={() => setListRefresh(value => value + 1)}>Retry locations</button></div>}
    {!loadingList && !listError && filtered.length === 0 && <p role="status">{practices.length ? 'No locations match your search.' : 'No locations are available for this account.'}</p>}
    {error && <div role="alert" className="lo-error">{error} <button onClick={() => setRefresh(value => value + 1)}>Retry status</button></div>}
    {!practiceId && <div className="lo-empty">Select a location to see its onboarding progress.</div>}
    {practiceId && loading && !status && <div role="status" className="lo-empty">Checking configuration and activity…</div>}
    {status && <>
      <div className="lo-summary"><div><h2>{status.name}</h2><p>{status.pms} · {status.practiceId}</p></div><div><strong>{complete} / {applicableCount}</strong><p>steps complete</p></div></div>
      <div className="lo-meta"><span>Checked {new Date(status.checkedAt).toLocaleString()} · Updates every minute{insuranceGroups.length ? ' · Zuub checks cached 15 min' : ''}</span><label><input type="checkbox" checked={onlyOutstanding} onChange={e => setOnlyOutstanding(e.target.checked)} /> Show outstanding only</label></div>
      {renderGroups(coreGroups)}
      {insuranceGroups.length > 0 && <section className="lo-section" aria-label="Insurance verification">
        <div className="lo-section-head">
          <div><p className="lo-eyebrow">INSURANCE VERIFICATION</p><h2>{insuranceTally.done} of {insuranceTally.total} steps</h2></div>
          {coverage && <div className="lo-coverage">
            <div className="lo-coverage-title"><span>Insured patients verifiable with connected portals</span><strong>{coverage.totalPct}%</strong></div>
            <div className="lo-coverage-bar" role="img" aria-label={`${coverage.totalPct}% verifiable: ${coverage.syncOnlyPct}% sync-only payers plus ${coverage.portalPct}% through connected portals`}>
              <span className="lo-coverage-sync" style={{ width: `${coverage.syncOnlyPct}%` }} />
              <span className="lo-coverage-portal" style={{ width: `${coverage.portalPct}%` }} />
              <span className="lo-coverage-target" style={{ left: `${coverage.targetPct}%` }} />
            </div>
            <div className="lo-coverage-legend"><span><i className="lo-coverage-sync" />Sync-only payers {coverage.syncOnlyPct}%</span><span><i className="lo-coverage-portal" />Connected portals +{coverage.portalPct}%</span><span>Target {coverage.targetPct}%</span></div>
          </div>}
        </div>
        {renderGroups(insuranceGroups)}
      </section>}
      {paymentsGroups.length > 0 && <section className="lo-section" aria-label="Payments">
        <div className="lo-section-head"><div><p className="lo-eyebrow">PAYMENTS</p><h2>{paymentsTally.done} of {paymentsTally.total} steps</h2></div></div>
        {renderGroups(paymentsGroups)}
      </section>}
      <p className="lo-footnote">Unknown means a source could not be read or the check is not yet defined. Completion reflects saved configuration and recorded activity, not an end-to-end service test.</p>
    </>}
  </section>;
}
